import { FC } from 'react';
import { Card, formatDate, withStyles } from 'vopay';
import type { VerificationStatus } from 'vopay';
import styles from './VerificationCardBank.module.scss';
import { Button } from '@/components/UI';
import { useTranslation, Trans } from 'react-i18next';
import { useDialog } from '@/providers/Dialog.provider';
import UpgradePlanBankDialog from '@/components/Customer/Tabs/PaymentMethods/UpgradePlanBankDialog/UpgradePlanBankDialog';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useClientAccountPaymentDetails, { type BankAccountDetails } from '@/hooks/usePaymentMethodDetails';
import { Link, useHistory } from 'react-router-dom';
import DialogLink from '@/components/Customer/Tabs/PaymentMethods/DialogLink/DialogLink';

const cn = withStyles(styles);

type VerificationCardBankProps = {
  bank: BankAccountDetails & {
    verificationStatus: VerificationStatus;
    verificationSentOn: string;
  };
  accountID: string;
  email: string;
  isLocked: boolean;
  onVerify: () => void;
  hasVerificationButton?: boolean;
  accountPhone?: string;
  accountAddress?: string;
  accountCity?: string;
  accountProvince?: string;
  accountPostalCode?: string;
  accountCountry?: string;
};

const VerificationCardBank: FC<VerificationCardBankProps> = ({
  bank,
  accountID,
  email,
  isLocked,
  onVerify,
  hasVerificationButton,
  accountPhone,
  accountAddress,
  accountCity,
  accountProvince,
  accountPostalCode,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.Details.Verification' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const dialog = useDialog();
  const alertDialog = useAlertDialog();
  const history = useHistory();

  const { verifyBankAccount } = useClientAccountPaymentDetails({
    accountID,
    email,
  });

  async function handleUnlockVerificationRequestClick() {
    await dialog.open<boolean>((args) => <UpgradePlanBankDialog {...args} />);
  }

  const handleSendVerification = async () => {
    try {
      // Use bank account phone/address if available, otherwise fall back to account info
      const phoneNumber = bank.account.Phone || accountPhone || '';
      const address = bank.account.Address || accountAddress || '';
      const city = bank.account.City || accountCity || '';
      const provinceState = bank.account['Province/State'] || accountProvince || '';
      const postalCodeZipCode = bank.account['PostalCode/ZipCode'] || accountPostalCode || '';

      const res = await verifyBankAccount({
        ...bank.account,
        Phone: phoneNumber,
        Address: address,
        City: city,
        'Province/State': provinceState,
        'PostalCode/ZipCode': postalCodeZipCode,
      });
      if (res.status === 'VERIFICATION_ELINX_SETTINGS_UNAVAILABLE') {
        alertDialog.caution(t('Dialog.error', { defaultValue: 'Error' }), res.message);
      } else if (res.status === 'VERIFICATION_UNAVAILABLE') {
        alertDialog.caution(
          t('Dialog.unavailable', { defaultValue: 'Verification Unavailable' }),
          <div>
            <Trans
              ns="translations"
              i18nKey="Customers.PaymentMethodsTab.Details.Verification.unavailableContent"
              components={{
                CustomLink: <Link to="/settings/elinx" className={cn('link')} />,
              }}
            />
          </div>,
          {
            abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
          },
        );
      } else if (res.status === 'VERIFICATION_FAIL') {
        alertDialog.caution(
          t('Dialog.failTitle', { defaultValue: 'Error! The verification request was not created.' }),
          res.message,
          {
            abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
          },
        );
      } else if (res.status === 'VERIFICATION_SEND_SUCCESS') {
        const closeType = await alertDialog.success(
          t('Dialog.sendSuccessTitle', { defaultValue: 'Verification Request Sent Successfully' }),
          res.link ? (
            <div
              style={{
                maxWidth: '486px',
                marginTop: '24px',
              }}
            >
              <DialogLink
                label={t('Dialog.elinxEmbeddedLink', { defaultValue: 'eLinx Embedded Link' })}
                link={res.link}
              />
            </div>
          ) : null,
          {
            abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
            confirmLabel: t('Dialog.goToActivity', { defaultValue: 'Go to Activity' }),
          },
        );

        if (closeType === 'confirm') {
          history.push({
            search: new URLSearchParams({
              cardTab: 'activity',
              activityType: 'Bank Verify History',
            }).toString(),
          });
        } else if (closeType === 'abort') {
          onVerify();
        }
      } else if (res.status === 'VERIFICATION_LINK_SUCCESS') {
        const closeType = await alertDialog.success(
          t('Dialog.linkCreatedTitle', { defaultValue: 'Link Created Successfully' }),
          <div>
            {res.link && (
              <div
                style={{
                  maxWidth: '486px',
                  marginTop: '24px',
                }}
              >
                <DialogLink
                  label={t('Dialog.elinxEmbeddedLink', { defaultValue: 'eLinx Embedded Link' })}
                  link={res.link}
                />
              </div>
            )}
          </div>,
          {
            abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
            confirmLabel: t('Dialog.goToActivity', { defaultValue: 'Go to Activity' }),
          },
        );
        if (closeType === 'confirm') {
          history.push({
            search: new URLSearchParams({
              cardTab: 'activity',
              activityType: 'Bank Verify History',
            }).toString(),
          });
        } else if (closeType === 'abort') {
          onVerify();
        }
      }
    } catch (err) {
      if (err instanceof Error) alertDialog.error('Error', err.message);

      console.error(err);
    }
  };

  return (
    <div className={cn('verificationCard', isLocked && 'fullyLocked')}>
      <Card style={{ padding: '16px' }} hoverEffect={false}>
        <span className={cn('cardContent')}>
          <div className={cn('header')}>
            <h5 className={cn('title')}>{t('title', { defaultValue: 'Verification Details' })}</h5>
            {bank.verificationSentOn && (
              <p className={cn('requestSentOn')}>
                {t('lastRequestSentOn', { defaultValue: 'Last request sent on' })}{' '}
                {formatDate(bank.verificationSentOn, 'yyyy-MM-dd HH:mm:ss')}
              </p>
            )}
          </div>
          <div>
            <div className={cn('row')}>
              <div>{t('status', { defaultValue: 'Verification Status' })}</div>
              <div>
                <p className={cn('verificationLabel', bank.verificationStatus)}>
                  {bank.verificationStatus === 'verified'
                    ? t('verified', { defaultValue: 'Verified' })
                    : bank.verificationStatus === 'unverified'
                      ? t('notVerified', { defaultValue: 'Not Verified' })
                      : t('failed', { defaultValue: 'Verification Failed' })}
                </p>
              </div>
            </div>
          </div>
          <div className={cn('footer')}>
            {!isLocked ? (
              <div className={cn('buttonContainer')}>
                {hasVerificationButton && (
                  <Button size="small" onClick={handleSendVerification}>
                    {t('sendRequestButton', { defaultValue: 'Send Verification Request' })}
                  </Button>
                )}
              </div>
            ) : (
              <div className={cn('buttonContainer')}>
                <Button
                  iconRightProps={{ icon: 'lock', fillAmount: 1 }}
                  size="small"
                  colorScheme="primary"
                  onClick={handleUnlockVerificationRequestClick}
                >
                  {t('unlockVerificationRequest', { defaultValue: 'Unlock Verification Request' })}
                </Button>
              </div>
            )}
          </div>
        </span>
      </Card>
    </div>
  );
};

export default VerificationCardBank;
